/**
 * Cracked Score: single 0–100 signal from review performance, strengths/weaknesses,
 * and confusion backlog hygiene. Pure functions for testability.
 */

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/**
 * @param {object} insights - API LearningInsightsResponse shape
 * @param {{ unresolved: number, total: number }} confusionMeta
 * @returns {{ score: number | null, headline: string, detail: string }}
 */
export function computeCrackedScore(insights, confusionMeta = { unresolved: 0, total: 0 }) {
  if (!insights) {
    return { score: null, headline: 'No data yet', detail: 'Complete reviews to unlock your score.' };
  }

  const answered = insights.total_questions_answered || 0;
  const overall = insights.overall_correct_rate ?? 0;
  const weak = (insights.weak_topics || []).length;
  const strong = (insights.strengths || []).length;
  const totalLogs = confusionMeta.total;
  const unresolved = confusionMeta.unresolved;

  if (answered === 0 && totalLogs === 0 && weak === 0 && strong === 0) {
    return {
      score: null,
      headline: 'Building baseline',
      detail: 'Answer a few review questions to see your Cracked Score.',
    };
  }

  // Base from overall accuracy when we have answers; otherwise neutral mid-band
  let base = answered > 0 ? overall * 0.55 : 52;
  const strengthLift = clamp(strong * 4, 0, 18);
  const weakDrag = clamp(weak * 5, 0, 22);

  let confusionAdj = 0;
  if (totalLogs > 0) {
    const resolvedRatio = (totalLogs - unresolved) / totalLogs;
    confusionAdj = (resolvedRatio - 0.5) * 14;
  }

  const score = clamp(Math.round(base + strengthLift - weakDrag + confusionAdj), 0, 100);

  let headline = 'On track';
  if (score >= 85) headline = 'Strong';
  else if (score >= 70) headline = 'Solid';
  else if (score >= 50) headline = 'Room to grow';
  else headline = 'Focus mode';

  const detailParts = [];
  if (answered > 0) detailParts.push(`${Math.round(overall)}% review accuracy`);
  if (weak > 0) detailParts.push(`${weak} weak topic${weak !== 1 ? 's' : ''}`);
  if (unresolved > 0) detailParts.push(`${unresolved} open confusion${unresolved !== 1 ? 's' : ''}`);

  return {
    score,
    headline,
    detail: detailParts.length ? detailParts.join(' · ') : 'Keep logging reviews and clearing confusions.',
  };
}
